'use client';

import { useState, useCallback } from 'react';
import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';
import { captureScreenshot, proxyImage } from '@/modules/export/screenshotService';
import { useDashboard } from '@/modules/dashboard/context/DashboardContext';

interface UseExportPageAsPdfOptions {
    pageTitle?: string;
}

// A4 landscape dimensions in mm
const A4_WIDTH_MM = 297;
const A4_HEIGHT_MM = 210;
const PAGE_MARGIN_MM = 6;
const CONTENT_WIDTH_MM = A4_WIDTH_MM - PAGE_MARGIN_MM * 2;
const CONTENT_HEIGHT_MM = A4_HEIGHT_MM - PAGE_MARGIN_MM * 2;

/**
 * Wait until all widget loading spinners are gone from the DOM.
 * Polls every 200ms, gives up after 15s.
 */
function waitForWidgetsToLoad(): Promise<void> {
    return new Promise((resolve) => {
        const MAX_WAIT = 15_000;
        const POLL_INTERVAL = 200;
        let elapsed = 0;

        const check = () => {
            const spinners = document.querySelectorAll('img[alt="Loading..."]');
            if (spinners.length === 0 || elapsed >= MAX_WAIT) {
                resolve();
                return;
            }
            elapsed += POLL_INTERVAL;
            setTimeout(check, POLL_INTERVAL);
        };

        check();
    });
}

/**
 * Expand all collapsed expandable table rows by clicking their chevrons.
 * Iterates to handle nested expansion levels.
 * Returns a cleanup function to collapse what was expanded.
 */
async function expandAllTableRows(): Promise<() => void> {
    const MAX_ITERATIONS = 5;

    for (let i = 0; i < MAX_ITERATIONS; i++) {
        const collapsedChevrons = Array.from(
            document.querySelectorAll('table path[d="M9 5l7 7-7 7"]')
        ).filter((path) => {
            const svg = path.parentElement;
            return svg && !svg.classList.contains('rotate-90');
        });

        if (collapsedChevrons.length === 0) break;

        collapsedChevrons.forEach((path) => {
            const row = path.closest('tr');
            if (row) row.click();
        });

        // Wait for React to re-render expansion rows
        await new Promise((r) => setTimeout(r, 500));
    }

    return () => {
        const expandedChevrons = Array.from(
            document.querySelectorAll('table path[d="M9 5l7 7-7 7"]')
        ).filter((path) => {
            const svg = path.parentElement;
            return svg && svg.classList.contains('rotate-90');
        });

        expandedChevrons.forEach((path) => {
            const row = path.closest('tr');
            if (row) row.click();
        });
    };
}

/**
 * Fetch backend screenshots for all iframes, then replace each <iframe>
 * with an <img> in the DOM so html2canvas captures them natively.
 * Returns a cleanup function to restore the original iframes.
 */
async function replaceIframesWithScreenshots(): Promise<() => void> {
    const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe');
    if (iframes.length === 0) return () => {};

    const entries: { iframe: HTMLIFrameElement; src: string; width: number; height: number }[] = [];
    for (const iframe of iframes) {
        if (!iframe.src) continue;
        const rect = iframe.getBoundingClientRect();
        entries.push({
            iframe,
            src: iframe.src,
            width: Math.round(rect.width) || 1200,
            height: Math.round(rect.height) || 800,
        });
    }

    if (entries.length === 0) return () => {};

    console.log('[PDF] Fetching screenshots for', entries.length, 'iframes');

    const results = await Promise.allSettled(
        entries.map(async (entry, i) => {
            console.log(`[PDF] Requesting screenshot #${i}:`, entry.src.substring(0, 80));
            const base64 = await captureScreenshot({
                url: entry.src,
                width: entry.width,
                height: entry.height,
            });
            console.log(`[PDF] Screenshot #${i} returned, base64 length: ${base64?.length ?? 0}`);
            return { ...entry, base64 };
        }),
    );

    const swaps: { iframe: HTMLIFrameElement; img: HTMLImageElement; prevDisplay: string }[] = [];

    for (const result of results) {
        if (result.status !== 'fulfilled') continue;
        const { iframe, base64, width, height } = result.value;

        const img = document.createElement('img');
        img.src = `data:image/png;base64,${base64}`;
        img.style.width = `${width}px`;
        img.style.height = `${height}px`;
        img.style.display = 'block';
        img.style.objectFit = 'cover';

        const prevDisplay = iframe.style.display;
        iframe.style.display = 'none';
        iframe.parentNode?.insertBefore(img, iframe);
        swaps.push({ iframe, img, prevDisplay });
        console.log(`[PDF] Replaced iframe with <img> (${width}x${height})`);
    }

    return () => {
        for (const { iframe, img, prevDisplay } of swaps) {
            iframe.style.display = prevDisplay;
            img.remove();
        }
    };
}

/**
 * Hide elements that should not appear in the PDF export.
 * Uses the same data attributes as the @media print CSS.
 * Returns a cleanup function to restore visibility.
 */
function hideNonPdfElements(): () => void {
    const selectors = [
        '[data-pdf-hide]',
        '[aria-label="Page settings"]',
        '[data-dropdown-portal]',
        '.fixed.inset-0.pointer-events-none',
    ];

    const hidden: { el: HTMLElement; prev: string }[] = [];

    for (const selector of selectors) {
        document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
            hidden.push({ el, prev: el.style.display });
            el.style.display = 'none';
        });
    }

    return () => {
        for (const { el, prev } of hidden) {
            el.style.display = prev;
        }
    };
}

interface WidgetHeightSnapshot {
    el: HTMLElement;
    height: number;
    prevHeight: string;
    prevMinHeight: string;
}

/**
 * Phase 1: Snapshot widget heights while layout is still intact.
 * Must be called BEFORE hiding sidebar / applying print styles.
 */
function snapshotWidgetHeights(): WidgetHeightSnapshot[] {
    const snapshots: WidgetHeightSnapshot[] = [];
    document.querySelectorAll<HTMLElement>('[class*="col-span-"]').forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.height > 0) {
            snapshots.push({
                el,
                height: rect.height,
                prevHeight: el.style.height,
                prevMinHeight: el.style.minHeight,
            });
        }
    });
    return snapshots;
}

/**
 * Phase 2: Apply snapshotted heights so Recharts ResponsiveContainer
 * keeps its sizing after layout changes.
 * Returns a cleanup function to restore original styles.
 */
function applyLockedHeights(snapshots: WidgetHeightSnapshot[]): () => void {
    for (const snap of snapshots) {
        snap.el.style.height = `${snap.height}px`;
        snap.el.style.minHeight = `${snap.height}px`;
    }
    return () => {
        for (const { el, prevHeight, prevMinHeight } of snapshots) {
            el.style.height = prevHeight;
            el.style.minHeight = prevMinHeight;
        }
    };
}

/**
 * Force elements with CSS fadeIn animations (inline opacity: 0) to their
 * final visible state.  html2canvas reads inline styles and does NOT
 * evaluate CSS animations, so animated cards (DataSources) appear invisible.
 * Also strips SVG <animate> elements so connector lines render at their
 * final position.
 * Returns a cleanup function.
 */
function forceAnimationsToFinalState(root: HTMLElement): () => void {
    const restorers: (() => void)[] = [];

    // Force inline-animated elements to visible
    root.querySelectorAll<HTMLElement>('[style*="opacity: 0"], [style*="opacity:0"]').forEach((el) => {
        const prevOpacity = el.style.opacity;
        const prevAnimation = el.style.animation;
        el.style.opacity = '1';
        el.style.animation = 'none';
        restorers.push(() => {
            el.style.opacity = prevOpacity;
            el.style.animation = prevAnimation;
        });
    });

    // Force SVG <animate> elements to their end state by removing them
    // (they keep strokeDashoffset at 1000, making paths invisible)
    root.querySelectorAll<SVGAnimateElement>('animate').forEach((anim) => {
        const parent = anim.parentElement;
        if (!parent) return;
        // Apply the "to" value before removing
        const attr = anim.getAttribute('attributeName');
        const to = anim.getAttribute('to');
        if (attr && to) {
            parent.setAttribute(attr, to);
        }
        anim.remove();
        restorers.push(() => {
            parent.appendChild(anim);
        });
    });

    return () => restorers.forEach((fn) => fn());
}

/**
 * Force-load lazy iframes so their src is set before we measure positions.
 * Returns a cleanup function.
 */
async function ensureIframesLoaded(): Promise<() => void> {
    const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe[loading="lazy"]');
    if (iframes.length === 0) return () => {};

    const originals: { el: HTMLIFrameElement; loading: 'eager' | 'lazy' }[] = [];
    iframes.forEach((iframe) => {
        originals.push({ el: iframe, loading: iframe.loading as 'eager' | 'lazy' });
        iframe.loading = 'eager';
    });

    // Give time for lazy iframes to start loading
    await new Promise((r) => setTimeout(r, 500));

    // Wait for each iframe to fire its load event (with timeout)
    await Promise.all(
        originals.map(
            ({ el }) =>
                new Promise<void>((resolve) => {
                    if (!el.src) { resolve(); return; }
                    const timer = setTimeout(resolve, 5000);
                    el.addEventListener('load', () => { clearTimeout(timer); resolve(); }, { once: true });
                }),
        ),
    );

    return () => {
        originals.forEach(({ el, loading }) => { el.loading = loading; });
    };
}

/**
 * Convert cross-origin <img> elements to inline data-URI sources so
 * html2canvas can capture them without CORS issues.
 * Returns a cleanup function to restore original src attributes.
 */
async function convertExternalImagesToDataUri(root: HTMLElement): Promise<() => void> {
    const imgs = root.querySelectorAll<HTMLImageElement>('img');
    const swaps: { img: HTMLImageElement; originalSrc: string }[] = [];

    await Promise.allSettled(
        Array.from(imgs).map(async (img) => {
            if (!img.src || img.src.startsWith('data:') || img.src.startsWith('blob:')) return;
            try {
                const url = new URL(img.src);
                if (url.origin === window.location.origin) return;
            } catch { return; }

            const originalSrc = img.src;
            try {
                const dataUri = await proxyImage(originalSrc);
                img.src = dataUri;
                swaps.push({ img, originalSrc });
            } catch (err) {
                console.warn('[PDF] Could not convert external image:', originalSrc.substring(0, 80), err);
            }
        }),
    );

    console.log(`[PDF] Converted ${swaps.length} external images to data URIs`);
    return () => {
        for (const { img, originalSrc } of swaps) {
            img.src = originalSrc;
        }
    };
}

/**
 * Convert inline SVG elements to <img> tags with data-URI sources.
 * html2canvas can struggle with complex SVGs; rasterising
 * them first guarantees they appear in the capture.
 * Returns a cleanup function to restore the originals.
 */
function rasterizeSvgs(root: HTMLElement): () => void {
    const swaps: { svg: SVGSVGElement; img: HTMLImageElement }[] = [];

    root.querySelectorAll<SVGSVGElement>('svg').forEach((svg) => {
        // Skip tiny SVGs (icons, etc.)
        if (svg.closest('[data-pdf-hide]')) return;
        const rect = svg.getBoundingClientRect();
        if (rect.width < 50 || rect.height < 20) return;
        // Skip SVGs with foreignObject (e.g. Funnel) — HTML inside foreignObject
        // gets stripped when serialized to a data-URI <img>, breaking the render
        if (svg.querySelector('foreignObject')) return;

        // Serialize SVG to a self-contained data URI
        const clone = svg.cloneNode(true) as SVGSVGElement;
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        // Always force pixel dimensions — percentage/em values don't work in data-URI <img>
        clone.setAttribute('width',  String(rect.width));
        clone.setAttribute('height', String(rect.height));

        const svgData = new XMLSerializer().serializeToString(clone);
        const dataUri = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);

        const img = document.createElement('img');
        img.src = dataUri;
        img.style.width  = `${rect.width}px`;
        img.style.height = `${rect.height}px`;
        img.style.display = 'block';

        svg.style.display = 'none';
        svg.parentNode?.insertBefore(img, svg);
        swaps.push({ svg, img });
    });

    return () => {
        for (const { svg, img } of swaps) {
            svg.style.display = '';
            img.remove();
        }
    };
}

/**
 * Apply temporary inline styles to match print layout for canvas capture.
 * Returns a cleanup function to restore original styles.
 */
function applyPrintLayoutStyles(): () => void {
    const restorers: (() => void)[] = [];

    // Helper to batch-apply inline styles and record undo
    const applyStyles = (selector: string, styles: Record<string, string>) => {
        document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
            const saved: Record<string, string> = {};
            for (const key of Object.keys(styles)) {
                saved[key] = el.style.getPropertyValue(key) || (el.style as any)[key] || '';
                (el.style as any)[key] = styles[key];
            }
            restorers.push(() => {
                for (const key of Object.keys(saved)) {
                    (el.style as any)[key] = saved[key];
                }
            });
        });
    };

    // Remove sticky/fixed positioning (exclude our overlay portal)
    applyStyles('.sticky:not([data-pdf-overlay]), .fixed:not([data-pdf-overlay])', { position: 'static' });

    // Collapse min-height on layout wrappers
    applyStyles('[data-pdf-layout]', { minHeight: '0', display: 'block' });
    applyStyles('[data-pdf-layout] > *', { minHeight: '0' });

    // Full width for content containers
    applyStyles('[data-pdf-content]', {
        width: '100%',
        maxWidth: '100%',
        paddingLeft: '0',
        paddingRight: '0',
        marginLeft: '0',
        marginRight: '0',
    });

    // Remove container max-width so widgets match the dealer banner width
    applyStyles('[data-pdf-content] .container', {
        maxWidth: '100%',
        paddingLeft: '0',
        paddingRight: '0',
    });

    // Force overflow visible on all scroll containers
    applyStyles('[style*="overflow"]', { overflowX: 'visible', overflowY: 'visible' });

    // Ensure col-span widgets don't clip, but keep overflow visible (NOT height:auto)
    applyStyles('[class*="col-span-"]', { overflow: 'visible' });

    return () => restorers.forEach((fn) => fn());
}

/**
 * Inject a dealer name banner at the top of the capture area so the
 * selected dealer appears in the exported PDF.
 */
function injectDealerBanner(contentEl: HTMLElement, dealerName: string): () => void {
    const banner = document.createElement('div');
    banner.style.cssText = [
        'width: 100%',
        'padding: 16px 24px',
        'background: #2D3A3F',
        'color: #ffffff',
        'font-family: Rajdhani, sans-serif',
        'font-size: 32px',
        'font-weight: 500',
        'border-radius: 8px',
        'margin-bottom: 12px',
    ].join(';');
    banner.textContent = dealerName;
    contentEl.prepend(banner);

    return () => banner.remove();
}

export function useExportPageAsPdf({ pageTitle = 'Dashboard' }: UseExportPageAsPdfOptions = {}) {
    const [isExporting, setIsExporting] = useState(false);
    const { state: { selectedDealer } } = useDashboard();

    const exportAsPdf = useCallback(async () => {
        // Wait for dropdown to fully close
        await new Promise((r) => setTimeout(r, 300));

        setIsExporting(true);

        let restoreHidden = () => {};
        let restoreLayout = () => {};
        let restoreIframes = () => {};
        let collapseRows = () => {};

        try {
            // 1. Wait for all widget data to finish loading (spinners gone)
            await waitForWidgetsToLoad();

            // 2. Expand all expandable table rows (needs clicks before pointer-events are disabled)
            collapseRows = await expandAllTableRows();

            // 2b. Force-load lazy iframes before we measure anything
            const restoreLazyIframes = await ensureIframesLoaded();

            // 3. Disable pointer events so hover states don't get captured
            document.body.style.pointerEvents = 'none';

            const contentEl = document.querySelector<HTMLElement>('[data-pdf-content]');
            if (!contentEl) {
                throw new Error('Could not find [data-pdf-content] element');
            }

            // 4. Snapshot widget heights BEFORE any layout changes
            const heightSnapshots = snapshotWidgetHeights();

            // 4b. Inject dealer name banner
            const removeDealerBanner = selectedDealer?.dealerName
                ? injectDealerBanner(contentEl, selectedDealer.dealerName)
                : () => {};

            // 5. Hide non-PDF elements (sidebar, header, dropdowns)
            restoreHidden = hideNonPdfElements();

            // 6. Apply print-like layout styles
            restoreLayout = applyPrintLayoutStyles();

            // 7. Apply the pre-change heights so widgets keep their sizing
            const lockRestore = applyLockedHeights(heightSnapshots);

            // 8. Replace iframes with backend screenshot <img> tags
            const restoreIframesFn = await replaceIframesWithScreenshots();

            // 9. Force CSS animations to final state (DataSources opacity:0 cards, SVG animates)
            const restoreAnimations = forceAnimationsToFinalState(contentEl);

            // 10. Convert SVGs to inline images for reliable capture
            const restoreSvgs = rasterizeSvgs(contentEl);

            // 10b. Convert external images to data URIs so html2canvas can capture them
            const restoreExternalImages = await convertExternalImagesToDataUri(contentEl);

            restoreIframes = () => { restoreIframesFn(); lockRestore(); restoreSvgs(); restoreAnimations(); restoreExternalImages(); restoreLazyIframes(); removeDealerBanner(); };

            // 11. Give layout + SVG images time to settle
            await new Promise((r) => setTimeout(r, 500));

            // 12. Capture the page with html2canvas (iframes already replaced with images)
            const SCALE = 1.5;
            const canvas = await html2canvas(contentEl, {
                scale: SCALE,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false,
                windowWidth: Math.max(contentEl.scrollWidth, 1280),
                windowHeight: contentEl.scrollHeight,
            });

            // 9. Generate PDF from canvas — avoid splitting widgets across pages
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
                compress: true,
            });

            const imgWidth = CONTENT_WIDTH_MM;
            const pxPerMm = canvas.width / imgWidth;
            const pageHeightPx = CONTENT_HEIGHT_MM * pxPerMm;

            if (canvas.height <= pageHeightPx) {
                // Single page
                const imgHeight = (canvas.height / canvas.width) * imgWidth;
                pdf.addImage(canvas.toDataURL('image/jpeg', 0.75), 'JPEG', PAGE_MARGIN_MM, PAGE_MARGIN_MM, imgWidth, imgHeight);
            } else {
                // Multi-page: smart page breaks + repeated table headers
                const contentRect = contentEl.getBoundingClientRect();

                // --- Collect all potential break points (widget bottoms + row bottoms) ---
                const breakPoints = new Set<number>();

                contentEl.querySelectorAll<HTMLElement>('[class*="col-span-"]').forEach((w) => {
                    const bottomPx = (w.getBoundingClientRect().bottom - contentRect.top) * SCALE;
                    if (bottomPx > 0 && bottomPx < canvas.height) breakPoints.add(bottomPx);
                });

                contentEl.querySelectorAll<HTMLElement>('tr').forEach((row) => {
                    const bottomPx = (row.getBoundingClientRect().bottom - contentRect.top) * SCALE;
                    if (bottomPx > 0 && bottomPx < canvas.height) breakPoints.add(bottomPx);
                });

                const sortedBreaks = Array.from(breakPoints).sort((a, b) => a - b);

                // Helper: find the largest value in a sorted array that is <= limit and > floor
                const findBestBreak = (limit: number, floor: number): number | null => {
                    for (let i = sortedBreaks.length - 1; i >= 0; i--) {
                        if (sortedBreaks[i] <= limit && sortedBreaks[i] > floor) return sortedBreaks[i];
                    }
                    return null;
                };

                // --- Build page slices ---
                const slices: { y: number; h: number }[] = [];
                let currentY = 0;

                while (currentY < canvas.height) {
                    const idealEnd = currentY + pageHeightPx;

                    if (idealEnd >= canvas.height) {
                        slices.push({ y: currentY, h: canvas.height - currentY });
                        break;
                    }

                    let bestBreak = findBestBreak(idealEnd, currentY);
                    if (!bestBreak) bestBreak = idealEnd;

                    slices.push({ y: currentY, h: bestBreak - currentY });
                    currentY = bestBreak;
                }

                // --- Render page slices ---
                for (let i = 0; i < slices.length; i++) {
                    if (i > 0) pdf.addPage();

                    const { y, h } = slices[i];
                    const pageCanvas = document.createElement('canvas');
                    pageCanvas.width = canvas.width;
                    pageCanvas.height = h;
                    const ctx = pageCanvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
                    }

                    const sliceImgHeight = h / pxPerMm;
                    const sliceData = pageCanvas.toDataURL('image/jpeg', 0.75);
                    // Free slice canvas memory immediately
                    pageCanvas.width = 0;
                    pageCanvas.height = 0;
                    pdf.addImage(
                        sliceData,
                        'JPEG',
                        PAGE_MARGIN_MM,
                        PAGE_MARGIN_MM,
                        imgWidth,
                        sliceImgHeight,
                    );
                }
            }

            // 10. Trigger download
            const sanitizedTitle = pageTitle.replace(/[^a-zA-Z0-9-_ ]/g, '').trim() || 'Dashboard';
            const timestamp = new Date().toISOString().slice(0, 10);
            pdf.save(`${sanitizedTitle}_${timestamp}.pdf`);
        } catch (err) {
            console.error('PDF export failed:', err);
        } finally {
            // 11. Restore everything
            restoreIframes();
            restoreLayout();
            restoreHidden();
            collapseRows();
            document.body.style.pointerEvents = '';
            setIsExporting(false);
        }
    }, [pageTitle, selectedDealer]);

    return { exportAsPdf, isExporting };
}
